import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { buildTemplateHtml } from "@/lib/minisite";
import { Zap, ExternalLink, AlertTriangle } from "lucide-react";

export default function Countdown() {
  const { code } = useParams();
  const [link, setLink] = useState(null);
  const [miniHtml, setMiniHtml] = useState(null);
  const [ad, setAd] = useState(null);
  const [settings, setSettings] = useState({ site_name: "kisalink.net" });
  const [count, setCount] = useState(7);
  const [error, setError] = useState(false);
  const [ready, setReady] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, s] = await Promise.all([
          api.get(`/public/link/${code}`),
          api.get(`/public/settings`).catch(() => ({ data: {} })),
        ]);
        setLink(data);
        setSettings(s.data || {});
        setCount(data.countdown_seconds || 7);
        document.title = data.og_title || data.title || "Yönlendiriliyor... — kisalink.net";
        try {
          const adRes = await api.get(`/public/ad`);
          if (adRes.data?.active) setAd(adRes.data);
        } catch {}
        setReady(true);
      } catch {
        // Not a short link — try mini site slug
        try {
          const { data: site } = await api.get(`/public/minisite/${code}`);
          document.title = site.title || "kisalink.net";
          setMiniHtml(site.mode === "html" ? site.html_content : buildTemplateHtml(site.template_data, { siteName: "kisalink.net" }));
        } catch {
          setError(true);
        }
      }
    })();
    // eslint-disable-next-line
  }, [code]);

  const redirect = async () => {
    try {
      const { data } = await api.post(`/public/link/${code}/click`);
      window.location.replace(data.target_url);
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    if (!ready || error || started.current) return;
    started.current = true;
    const timer = setInterval(() => {
      setCount((c) => {
        if (c <= 1) {
          clearInterval(timer);
          redirect();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [ready, error]);

  const clickAd = async () => {
    if (!ad) return;
    try { await api.post(`/public/ad/${ad.id}/click`); } catch {}
    window.open(ad.target_url, "_blank", "noopener");
  };

  if (miniHtml !== null) {
    return (
      <iframe
        title="mini-site"
        srcDoc={miniHtml}
        sandbox="allow-scripts allow-popups allow-forms allow-same-origin allow-modals"
        className="w-full h-screen border-0"
        data-testid="public-minisite-frame"
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500" />
        <h1 className="mt-4 text-2xl font-bold font-display">Bağlantı bulunamadı</h1>
        <p className="mt-2 text-slate-500">Bu kısa link veya sayfa geçersiz yahut pasif durumda.</p>
        <a href="/" className="mt-5 text-blue-600 font-semibold">Ana sayfaya dön</a>
      </div>
    );
  }

  const total = link?.countdown_seconds || 7;
  const progress = ((total - count) / total) * 100;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="h-16 flex items-center justify-center border-b border-slate-200 bg-white/70 backdrop-blur">
        <span className="flex items-center gap-2 font-display font-extrabold">
          <span className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center"><Zap className="w-4 h-4 text-white" fill="white" /></span>
          {settings.site_name || "kisalink.net"}
        </span>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <p className="text-slate-500 mb-6">Lütfen bekleyin, yönlendiriliyorsunuz...</p>

        <div className="relative w-32 h-32" data-testid="countdown-timer-display">
          <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="52" fill="none" stroke="#E2E8F0" strokeWidth="10" />
            <circle cx="60" cy="60" r="52" fill="none" stroke="#2563EB" strokeWidth="10" strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 52}
              strokeDashoffset={(2 * Math.PI * 52) * (1 - progress / 100)}
              style={{ transition: "stroke-dashoffset 1s linear" }} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-5xl font-extrabold text-blue-600 font-display">{count}</span>
          </div>
        </div>

        {(link?.title || link?.og_title) && (
          <p className="mt-6 text-lg font-semibold text-slate-800 max-w-md text-center">{link.title || link.og_title}</p>
        )}

        <div className="mt-8 w-full max-w-md" data-testid="ad-banner-slot">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 text-center mb-2">Sponsor Reklamı</p>
          {ad ? (
            <button onClick={clickAd} className="block w-full rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg transition-shadow" data-testid="ad-banner-link">
              {ad.image_url ? (
                <img src={fileUrl(ad.image_url)} alt={ad.title || "Reklam"} className="w-full h-auto object-cover" loading="lazy" />
              ) : (
                <div className="p-8 flex items-center justify-center gap-2 text-blue-600 font-semibold">
                  {ad.title || "Reklamı Görüntüle"} <ExternalLink className="w-4 h-4" />
                </div>
              )}
            </button>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center text-slate-400 text-sm">
              Reklam alanı
            </div>
          )}
        </div>

        <p className="mt-6 text-xs text-slate-400">Geri sayım tamamlandığında otomatik yönlendirileceksiniz.</p>
      </div>
    </div>
  );
}
